import { useRef, useState } from "react"

import { Button } from "@coval/ui"
import { Pause, Play } from "lucide-react"

export default function MiniAudioPlayer({ audioUrl }: { audioUrl: string }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const { currentTime, duration } = audioRef.current
      setProgress((currentTime / duration) * 100)
    }
  }

  const togglePlay = async () => {
    if (audioRef.current) {
      try {
        setError(false)
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          const audio = audioRef.current
          if (audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            setError(true)
            setIsLoading(false)
            return
          }

          if (audio.readyState === 0) {
            setIsLoading(true)
            audio.load()
          }

          if (audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            setError(true)
            setIsLoading(false)
            return
          }

          await audio.play()
        }
        setIsPlaying(!isPlaying)
      } catch {
        setError(true)
        setIsLoading(false)
      }
    }
  }

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      audioRef.current &&
      !isNaN(audioRef.current.duration) &&
      audioRef.current.duration > 0
    ) {
      const progressBar = e.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const clickPosition = (e.clientX - rect.left) / rect.width
      const newTime = clickPosition * audioRef.current.duration

      if (!isNaN(newTime) && isFinite(newTime) && newTime >= 0) {
        audioRef.current.currentTime = newTime
        setProgress(clickPosition * 100)
      }
    }
  }

  return (
    <div className="group flex items-center gap-2">
      <Button
        icon
        onClick={() => {
          void togglePlay()
        }}
        className={`min-w-8 w-8 h-8 ${error ? "bg-red-100 text-red-500" : ""}`}
        disabled={isLoading}
        tooltip={isPlaying ? "Pause audio" : "Play audio"}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
      </Button>
      <div
        className={`w-24 group-hover:w-32 group-hover:h-2 h-1 ${
          error ? "bg-red-100" : "bg-gray-200"
        } rounded-full overflow-hidden cursor-pointer transition-all duration-200`}
        onClick={handleProgressBarClick}
      >
        <div
          className={`h-full ${
            error ? "bg-red-500" : "bg-coval-progress"
          } transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={() => setIsPlaying(false)}
        onLoadedData={() => {
          setIsLoading(false)
          setError(false)
        }}
        onError={() => {
          setIsLoading(false)
          setError(true)
        }}
        onTimeUpdate={handleTimeUpdate}
        className="hidden"
      />
    </div>
  )
}
